import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Card, CoreApiMinimal, CreditCardConfig, TokenExCardTypes } from '../../api';
import { useBreakPoints } from '../useBreakPoints';
import { CvvID, NumberID, iFrameConfig } from './utils';

interface Validate {
  cvvValidator: 'required';
  isCvvValid: boolean;
  isValid: boolean;
  validator: 'required';
  cardType?: TokenExCardTypes;
}

type Errors = {
  isValid: boolean;
  isError: boolean;
  message: string;
};

export type TokenExAutoFillValues = {
  nameOnCard: string;
  cardExp: string;
};

// Load Token Ex Iframe
const useLoadTokenEx = () => {
  const [isTokenExLoaded, setIsTokenExLoaded] = useState(false);

  const onLoad = () => setIsTokenExLoaded(true);

  useEffect(() => {
    const tokenEx = document.createElement('script');
    const environment = import.meta.env.VITE_API_ENV || 'staging'; // eslint-disable-line no-undef
    const url =
      environment === 'production'
        ? 'https://htp.tokenex.com/iframe/iframe-v3.min.js'
        : 'https://test-htp.tokenex.com/Iframe/Iframe-v3.min.js';
    tokenEx.setAttribute('src', url);
    tokenEx.addEventListener('load', onLoad);

    document.head.appendChild(tokenEx);
  }, []);

  return { isTokenExLoaded };
};

interface UseTokenEx {
  onTokenize?: (params: any) => void;
  onAutoFill?: (autoFillValues: TokenExAutoFillValues) => void;
  useRemoteConfig?: boolean;
  useCvv?: boolean;
  tokenizeOnValidate?: boolean;
}

interface TokenExSettingBase {
  config?: CreditCardConfig;
  cardType?: string;
  useCvv: boolean;
  tokenizeOnValidate: boolean;
}
interface TokenExSettingCvvOnly extends TokenExSettingBase {
  cardType: string;
}
interface TokenExSettingFullCard extends TokenExSettingBase {
  cardType?: never;
}

type TokenExSetting = TokenExSettingCvvOnly | TokenExSettingFullCard;

const useTokenExErrors = ({
  card,
  validate,
  cvvOnly = false,
}: {
  card?: Card;
  validate?: Validate;
  cvvOnly?: boolean;
}) => {
  const [errorStates, setErrorStates] = useState<Errors>({
    isValid: false,
    isError: false,
    message: '',
  });

  useEffect(() => {
    // If both values are true, get outta here
    if (validate?.isValid && validate?.isCvvValid)
      return setErrorStates({ isValid: true, isError: false, message: '' });

    if (validate === undefined) {
      setErrorStates({
        isValid: false,
        isError: false,
        message: '',
      });
    } else if (validate?.isCvvValid === false) {
      setErrorStates({
        isValid: false,
        isError: true,
        message: 'CVV is invalid',
      });
    } else if (validate?.isValid == false) {
      // if isCvvValid === undefined we should assume we don't need a cvv so it's valid to say that card or CVV is invalid.... depending on the next part
      const message = cvvOnly ? 'CVV is invalid' : 'Credit Card is missing or invalid';
      setErrorStates({
        isValid: false,
        isError: true,
        message: message,
      });
    } else if (card) {
      setErrorStates({
        isValid: true,
        isError: false,
        message: '',
      });
    }
  }, [card, validate]);

  return { errorStates };
};

type configKeys = 'cvvOnly' | 'creditCard';

interface TokenExConfigBase {
  enableAutoComplete: boolean;
  enablePrettyFormat: boolean;
  enableValidateOnBlur: boolean;
  origin: string;
  pci: boolean;
  placeholder: string;
  cvv: boolean;
  cvvContainerID: string;
  cvvPlaceholder: string;
  styles: {
    base: string;
    cvv?: { base: string };
  };
}

interface TokenExConfigCvvOnly extends TokenExConfigBase {
  styles: {
    base: string;
    cvv?: never;
  };
}

type TokenExConfigFullCard = TokenExConfigBase;

type TokenExConfig = TokenExConfigCvvOnly | TokenExConfigFullCard;

type TokenExConfigs = {
  [configKey in configKeys]: {
    id: typeof CvvID | typeof NumberID;
    config: TokenExConfig;
    onBlur: () => void;
    onChange: () => void;
    autoCompleteValues: (autoCompleteValues: TokenExAutoFillValues) => void;
    onValidate: ({
      cardValidity,
      callback,
    }: {
      cardValidity: Validate;
      callback?: () => void;
    }) => void;
    isComplete: (card?: Partial<Card>) => boolean;
    isValid: (validate: Validate) => boolean;
  };
};

// Could optimize the memoization/callbacks here.
export const useTokenEx = ({
  onTokenize,
  useRemoteConfig,
  useCvv,
  onAutoFill,
  tokenizeOnValidate = true,
}: UseTokenEx) => {
  /** Current Tokenized card */
  const [card, setCard] = useState<undefined | Card>();
  /** Current Card Type */
  const [cardType, setCardType] = useState<undefined | TokenExCardTypes>();
  const [settings, setSettings] = useState<TokenExSetting>({
    useCvv: useCvv ?? iFrameConfig.cvv,
    tokenizeOnValidate: tokenizeOnValidate,
  });
  const [validate, setValidate] = useState<undefined | Validate>();
  const [configType, setConfigType] = useState<undefined | configKeys>();
  const { isMobile } = useBreakPoints();
  const { isTokenExLoaded } = useLoadTokenEx();
  const { errorStates } = useTokenExErrors({
    validate,
    card,
    cvvOnly: configType == 'cvvOnly',
  });

  const iframe = useRef<null | any>(null);

  const fetchRemoteConfig = useCallback(async () => {
    const { data }: { data: CreditCardConfig } = await CoreApiMinimal.get('/payment_methods/new');
    setSettings((prev) => ({ ...prev, config: data }));
  }, []);

  const tokenExConfigs: TokenExConfigs = useMemo(
    () => ({
      cvvOnly: {
        id: CvvID,
        config: {
          ...iFrameConfig,
          returnAutoCompleteValues: false,
          placeholder: isMobile ? 'CVV' : 'Enter CVV',
          cvvOnly: true,
          token: settings?.config?.scheme,
          cardType: settings?.cardType,
          tokenScheme: settings?.config?.scheme,
          timestamp: settings?.config?.timestamp,
          tokenExID: settings?.config?.app_id,
          authenticationKey: settings?.config?.authentication_key,
        },
        onBlur: () => {
          iframe.current.validate();
        },
        onChange: () => {
          setValidate(undefined);
        },
        autoCompleteValues: (autoCompleteValues) => {
          onAutoFill?.(autoCompleteValues);
        },
        onValidate: ({ cardValidity }) => {
          setValidate(cardValidity);
          if (cardValidity.isValid) {
            settings.tokenizeOnValidate && iframe.current.tokenize();
          } else {
            setCard(undefined);
          }
        },
        isComplete: (card) => !!card?.token,
        isValid: (validate) => !!validate?.isValid, // When cvv only, card validity only returns an isValid check
      },
      creditCard: {
        id: NumberID,
        config: {
          ...iFrameConfig,
          tokenScheme: settings?.config?.scheme,
          timestamp: settings?.config?.timestamp,
          tokenExID: settings?.config?.app_id,
          authenticationKey: settings?.config?.authentication_key,
          cvv: settings.useCvv,
        },
        onBlur: () => {
          iframe.current.validate();
        },
        onChange: () => {
          setValidate(undefined);
        },
        autoCompleteValues: (autoCompleteValues) => {
          onAutoFill?.(autoCompleteValues);
        },
        onValidate: ({ cardValidity }) => {
          /**
           * If the card type has changed, update the card type
           * It is intentional that we update the card type even when it is undefined
           * This is to ensure that the card type is always up to date with the latest card type
           */
          if (cardValidity?.cardType !== cardType) {
            setCardType(cardValidity?.cardType);
          }
          setValidate(cardValidity);
          const isCardValid = cardValidity.isValid;
          const isCvvValid = settings?.useCvv === true ? cardValidity?.isCvvValid : true;

          if (isCardValid && isCvvValid) {
            settings.tokenizeOnValidate && iframe.current.tokenize();
          } else {
            setCard(undefined);
          }
        },
        isComplete: (card) => !!(card?.token && card?.tokenHMAC),
        isValid: (cardValidity) => {
          const isCardValid = cardValidity.isValid;
          const isCvvValid = settings?.useCvv === true ? cardValidity?.isCvvValid : true;
          return isCardValid && isCvvValid;
        },
      },
    }),
    [settings, iFrameConfig]
  );

  const initiateTokenEx = useCallback(
    (type: configKeys) => {
      // Don't allow multiple instances of the tokenEx to be created
      if (iframe.current) {
        console.warn('Whoops! To many instances!');
        return false;
      }
      setConfigType(type);
      const tokenEx = new window.TokenEx.Iframe(
        tokenExConfigs[type].id,
        tokenExConfigs[type].config
      );
      if (!tokenEx) return;
      tokenEx.load();

      tokenEx.on('cvvBlur', () => {
        tokenExConfigs[type]?.onBlur?.();
      });

      tokenEx.on('blur', () => {
        tokenExConfigs[type]?.onBlur?.();
      });

      tokenEx.on('change', () => {
        tokenExConfigs[type]?.onChange?.();
      });

      tokenEx.on('autoCompleteValues', (autoFillValues: TokenExAutoFillValues) => {
        tokenExConfigs[type]?.autoCompleteValues?.(autoFillValues);
      });

      tokenEx.on('tokenize', (response: Card) => {
        onTokenize?.(response);
        setCard(response);
        iframe.current.tokenizePromiseResolve?.(response);
        return response;
      });

      tokenEx.on('validate', (cardValidity: Validate) => {
        tokenExConfigs[type]?.onValidate?.({
          cardValidity,
        }); // When card passes config validation, tokenize
      });
      iframe.current = tokenEx;
    },
    [tokenExConfigs, onTokenize, setConfigType]
  );

  const isTokenExConfigReady = useMemo(
    () =>
      !!(
        settings?.config?.app_id &&
        settings?.config?.timestamp &&
        settings?.config?.authentication_key &&
        settings?.config?.scheme
      ),
    [settings]
  );

  const isConfigAndTokenReady = useMemo(
    () => !!(isTokenExConfigReady && isTokenExLoaded),
    [isTokenExLoaded, isTokenExConfigReady]
  );

  const isCardComplete = useMemo(() => {
    if (configType && card) {
      return tokenExConfigs[configType].isComplete(card);
    }
    return false;
  }, [card]);

  const isCardValid = useMemo(() => {
    if (configType && validate) {
      return tokenExConfigs[configType].isValid(validate);
    }
    return false;
  }, [validate]);

  useEffect(() => {
    if (useRemoteConfig) {
      fetchRemoteConfig();
    }
  }, [useRemoteConfig]);

  useEffect(() => {
    return () => {
      iframe?.current?.remove?.();
    };
  }, []);

  return {
    cardType,
    iframe,
    isConfigAndTokenReady,
    setSettings,
    initiateTokenEx,
    errorStates,
    card,
    isCardComplete,
    isCardValid,
    validate,
    tokenizeCard: useCallback(async () => {
      if (!isCardValid) return;
      return new Promise(function (resolve) {
        iframe.current.tokenizePromiseResolve = (data: Card) => resolve(data);
        iframe.current.tokenize();
      })
        .then((card) => card)
        .catch((error) => console.log('error', error))
        .finally(() => (iframe.current.tokenizePromiseResolve = null));
    }, [isCardValid]),
  };
};
